import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { CustomerForm } from '../components/checkout/CustomerForm';
import { ShippingForm } from '../components/checkout/ShippingForm';
import { PaymentSection } from '../components/checkout/PaymentSection';
import { OrderSummary } from '../components/checkout/OrderSummary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [pricing, setPricing] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);

  const [discountCode, setDiscountCode] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', phone: '' });
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });

  useEffect(() => {
    if (cart.length === 0) navigate('/cart');
  }, [cart, navigate]);

  useEffect(() => {
    const calculatePricing = async () => {
      try {
        const response = await axios.post(`${API}/checkout/calculate`, {
          items: cart,
          shipping_address: shippingAddress,
        });
        let pricingData = response.data;
        if (appliedDiscount) {
          pricingData = {
            ...pricingData,
            discount: appliedDiscount.discount_amount,
            discount_code: appliedDiscount.code,
            total: pricingData.total - appliedDiscount.discount_amount,
          };
        }
        setPricing(pricingData);
      } catch (error) {
        console.error('Error calculating pricing:', error);
      }
    };
    if (cart.length > 0 && shippingAddress.state) {
      calculatePricing();
    }
  }, [cart, shippingAddress.state, appliedDiscount]);

  useEffect(() => {
    if (!stripe || !pricing) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Total', amount: Math.round(pricing.total * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: 'standard',
          label: 'Standard Shipping',
          detail: '5-10 business days',
          amount: Math.round(pricing.shipping * 100),
        },
      ],
    });

    pr.canMakePayment().then((result) => {
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (ev) => {
      try {
        setCustomerInfo({
          name: ev.payerName,
          email: ev.payerEmail,
          phone: ev.payerPhone || '',
        });

        if (ev.shippingAddress) {
          setShippingAddress({
            line1: ev.shippingAddress.addressLine[0] || '',
            line2: ev.shippingAddress.addressLine[1] || '',
            city: ev.shippingAddress.city,
            state: ev.shippingAddress.region,
            postal_code: ev.shippingAddress.postalCode,
            country: ev.shippingAddress.country,
          });
        }

        const { data } = await axios.post(`${API}/stripe/create-payment-intent`, {
          items: cart,
          customer_info: {
            name: ev.payerName,
            email: ev.payerEmail,
            phone: ev.payerPhone || '',
          },
          shipping_address: {
            line1: ev.shippingAddress?.addressLine[0] || '',
            line2: ev.shippingAddress?.addressLine[1] || '',
            city: ev.shippingAddress?.city || '',
            state: ev.shippingAddress?.region || '',
            postal_code: ev.shippingAddress?.postalCode || '',
            country: ev.shippingAddress?.country || 'US',
          },
          ...pricing,
        });

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          toast.error(confirmError.message);
        } else {
          ev.complete('success');

          if (paymentIntent.status === 'requires_action') {
            const { error } = await stripe.confirmCardPayment(data.client_secret);
            if (error) {
              toast.error(error.message);
              return;
            }
          }

          await axios.post(`${API}/stripe/confirm-payment`, {
            payment_intent_id: paymentIntent.id,
            order_details: {},
          });

          clearCart();
          toast.success('Payment successful!');
          navigate(`/order-confirmation/${data.order_number}`);
        }
      } catch (error) {
        ev.complete('fail');
        toast.error('Payment failed');
        console.error(error);
      }
    });
  }, [stripe, pricing, cart, clearCart, navigate]);

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    if (!pricing) {
      toast.error('Please enter shipping address first');
      return;
    }

    setDiscountLoading(true);
    try {
      const response = await axios.post(`${API}/discounts/validate`, {
        code: discountCode,
        subtotal: pricing.subtotal,
      });

      if (response.data.valid) {
        setAppliedDiscount({
          code: response.data.code,
          discount_amount: response.data.discount_amount,
          discount_type: response.data.discount_type,
        });
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to validate discount code');
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!customerInfo.name || !customerInfo.email) {
      toast.error('Please fill in customer information');
      return false;
    }
    if (!shippingAddress.line1 || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postal_code) {
      toast.error('Please fill in shipping address');
      return false;
    }
    return true;
  };

  const handleStripePayment = async (e) => {
    e.preventDefault();
    if (!validateForm() || !stripe || !elements) return;
    if (!pricing) {
      toast.error('Please wait while we calculate your total');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/stripe/create-payment-intent`, {
        items: cart,
        customer_info: customerInfo,
        shipping_address: shippingAddress,
        ...pricing,
      });

      const { error, paymentIntent } = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
            address: {
              line1: shippingAddress.line1,
              line2: shippingAddress.line2,
              city: shippingAddress.city,
              state: shippingAddress.state,
              postal_code: shippingAddress.postal_code,
              country: shippingAddress.country,
            },
          },
        },
        shipping: {
          name: customerInfo.name,
          phone: customerInfo.phone,
          address: {
            line1: shippingAddress.line1,
            line2: shippingAddress.line2,
            city: shippingAddress.city,
            state: shippingAddress.state,
            postal_code: shippingAddress.postal_code,
            country: shippingAddress.country,
          },
        },
      });

      if (error) {
        toast.error(error.message);
        setLoading(false);
      } else if (paymentIntent.status === 'succeeded') {
        await axios.post(`${API}/stripe/confirm-payment`, {
          payment_intent_id: paymentIntent.id,
          order_details: {},
        });
        clearCart();
        toast.success('Payment successful!');
        navigate(`/order-confirmation/${data.order_number}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    if (!validateForm()) throw new Error('Please fill in all required fields');
    if (!pricing) {
      toast.error('Please wait while we calculate your total');
      throw new Error('Pricing not calculated');
    }

    try {
      const { data } = await axios.post(`${API}/paypal/create-order`, {
        items: cart,
        customer_info: customerInfo,
        shipping_address: shippingAddress,
        ...pricing,
      });
      return data.order_id;
    } catch (error) {
      toast.error('Failed to create PayPal order');
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/paypal/capture-order`, {
        paypal_order_id: data.orderID,
      });
      clearCart();
      toast.success('Payment successful!');
      navigate(`/order-confirmation/${response.data.order.order_number}`);
    } catch (error) {
      toast.error('Failed to capture payment');
      console.error('PayPal capture error:', error);
    }
  };

  if (cart.length === 0) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-5xl font-bold text-white mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <CustomerForm customerInfo={customerInfo} onChange={handleCustomerChange} />
            <ShippingForm shippingAddress={shippingAddress} onChange={handleShippingChange} />
            <PaymentSection
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentRequest={paymentRequest}
              pricing={pricing}
              stripe={stripe}
              loading={loading}
              onStripeSubmit={handleStripePayment}
              createPayPalOrder={createPayPalOrder}
              onPayPalApprove={onPayPalApprove}
            />
          </div>

          <div className="lg:col-span-1">
            <OrderSummary
              cart={cart}
              pricing={pricing}
              appliedDiscount={appliedDiscount}
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              discountLoading={discountLoading}
              applyDiscountCode={applyDiscountCode}
              removeDiscount={removeDiscount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
